import { resolveFormGroups } from '@common/utils/formGroups';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { updateTemplateFormProperties } from '@/services/TemplateFormsService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import { buildIntakeFormPropertiesPayload } from './intakeFormPayload';
import type { IntakeDesignData } from './types';

interface Props {
    quoteTemplateId: number | null;
    intakeDesign: IntakeDesignData;
    setIntakeDesign: Dispatch<SetStateAction<IntakeDesignData>>;
    onClose: () => void;
}

export default function AddGroupDialog({
    quoteTemplateId,
    intakeDesign,
    setIntakeDesign,
    onClose,
}: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const [groupName, setGroupName] = useState('');

    const onAdd = async () => {
        const trimmedGroupName = groupName.trim();
        if (!trimmedGroupName) {
            errorSnackbar('Group name cannot be empty');
            return;
        }

        const existingGroups = resolveFormGroups(
            intakeDesign.formGroups,
            intakeDesign.templateFormFields,
        );
        const groupExists = existingGroups.some(
            (g) => g.name.toLowerCase() === trimmedGroupName.toLowerCase(),
        );
        if (groupExists) {
            errorSnackbar('A group with this name already exists');
            return;
        }

        const maxDisplayOrder = existingGroups.reduce((max, g) => Math.max(max, g.displayOrder), 0);

        const newGroup = {
            name: trimmedGroupName,
            displayOrder: maxDisplayOrder + 1,
        };

        const updatedFormGroups = [...existingGroups, newGroup];

        const updatedIntakeDesign = {
            ...intakeDesign,
            formGroups: updatedFormGroups,
            currentIntakeTab: {
                tab: updatedFormGroups.length - 1,
                tabLabel: trimmedGroupName,
            },
        };

        setIntakeDesign(updatedIntakeDesign);

        if (!quoteTemplateId || !intakeDesign.templateFormId) {
            successSnackbar('Group added successfully');
            onClose();
            return;
        }

        setIsLoading(true);
        try {
            await updateTemplateFormProperties(
                quoteTemplateId,
                intakeDesign.templateFormId,
                // Groups can only be edited on V2 forms
                buildIntakeFormPropertiesPayload({
                    intakeDesign,
                    isIntakeFormV2: true,
                    formGroups: updatedFormGroups,
                }),
            );
            successSnackbar('Group added successfully');
            onClose();
        } catch (error: any) {
            setIntakeDesign(intakeDesign);
            console.error(error);
            errorSnackbar(error.response?.data || 'Something went wrong!');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add new group</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    margin="dense"
                    label="Group name"
                    value={groupName}
                    onChange={(event) => setGroupName(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            onAdd();
                        }
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={onAdd} disabled={isLoading}>
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
}
